import React, { useState, useEffect } from "react";
import {
	collection,
	addDoc,
	query,
	where,
	getDocs,
	deleteDoc,
	doc,
	getDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import Papa from "papaparse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./Auth.css";

const isAdminEmail = (email) => {
	const adminEmails = process.env.REACT_APP_ADMIN_EMAIL.replace("[", "")
		.replace("]", "")
		.split(",")
		.map((email) => email.trim().toLowerCase());
	return adminEmails.includes(email.toLowerCase());
};

function AdminUpload() {
	const [file, setFile] = useState(null);
	const [adminEmail, setAdminEmail] = useState("");
	const [adminPassword, setAdminPassword] = useState("");
	const [newEmail, setNewEmail] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);
	const [showCredentialsModal, setShowCredentialsModal] = useState(false);
	const [pendingAction, setPendingAction] = useState(null);
	const [authorizedUsers, setAuthorizedUsers] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState(new Set());
	const [activeView, setActiveView] = useState("add"); // 'add' or 'manage'
	const navigate = useNavigate();

	useEffect(() => {
		fetchAuthorizedUsers();
	}, []);

	const fetchAuthorizedUsers = async () => {
		try {
			const q = query(collection(db, "authorizedEmails"));
			const querySnapshot = await getDocs(q);
			const users = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			setAuthorizedUsers(users);
		} catch (error) {
			setError("Error fetching authorized users: " + error.message);
		}
	};

	const handleDeleteUsers = async () => {
		try {
			const usersToDelete = Array.from(selectedUsers);
			let deletedCount = 0;

			for (const userId of usersToDelete) {
				const userDoc = await getDoc(doc(db, "authorizedEmails", userId));
				const userData = userDoc.data();

				// Double check - skip if user is admin or their email is in ADMIN_EMAIL
				if (userData.isAdmin || isAdminEmail(userData.email)) {
					setError("Cannot delete admin users");
					continue;
				}

				// Check if user exists in users collection
				const userQuery = query(
					collection(db, "users"),
					where("email", "==", userData.email)
				);
				const userQuerySnapshot = await getDocs(userQuery);

				// Delete from users collection if exists
				if (!userQuerySnapshot.empty) {
					await deleteDoc(doc(db, "users", userQuerySnapshot.docs[0].id));
				}

				// Delete from authorizedEmails
				await deleteDoc(doc(db, "authorizedEmails", userId));
				deletedCount++;
			}

			setSuccess(`Successfully removed ${deletedCount} users`);
			setSelectedUsers(new Set()); // Clear selections
			fetchAuthorizedUsers(); // Refresh the list
		} catch (error) {
			setError("Error removing users: " + error.message);
		}
	};

	const handleUserSelection = (userId) => {
		// Get the user data first
		const user = authorizedUsers.find((user) => user.id === userId);

		// Don't allow selection if user is admin or in admin email list
		if (user.isAdmin || isAdminEmail(user.email)) {
			return;
		}

		const newSelected = new Set(selectedUsers);
		if (newSelected.has(userId)) {
			newSelected.delete(userId);
		} else {
			newSelected.add(userId);
		}
		setSelectedUsers(newSelected);
	};

	const handleAdminAuth = async () => {
		// First check if any users exist
		const usersQuery = query(collection(db, "users"));
		const usersSnapshot = await getDocs(usersQuery);

		// If no users exist, this is initial setup
		if (usersSnapshot.empty) {
			// Add admin email to authorized emails
			const adminAuthQuery = query(
				collection(db, "authorizedEmails"),
				where("email", "==", adminEmail.toLowerCase())
			);
			const adminAuthSnapshot = await getDocs(adminAuthQuery);

			if (adminAuthSnapshot.empty) {
				await addDoc(collection(db, "authorizedEmails"), {
					email: adminEmail.toLowerCase(),
					registered: false,
					createdAt: new Date(),
					isAdmin: true,
				});
			}
			return true;
		}

		// Otherwise, check if email is admin
		if (!isAdminEmail(adminEmail)) {
			setError("Unauthorized access");
			return false;
		}

		if (!adminPassword) {
			setError("Password is required");
			return false;
		}

		return true;
	};

	const handleActionRequest = (actionType) => {
		setShowCredentialsModal(true);
		setPendingAction(actionType);
	};

	const addSingleEmail = async () => {
		if (!newEmail) return;
		setLoading(true);
		setError("");
		setSuccess("");

		try {
			const q = query(
				collection(db, "authorizedEmails"),
				where("email", "==", newEmail.toLowerCase())
			);
			const querySnapshot = await getDocs(q);

			if (querySnapshot.empty) {
				// Add the email to authorizedEmails collection
				// eslint-disable-next-line
				const docRef = await addDoc(collection(db, "authorizedEmails"), {
					email: newEmail.toLowerCase(),
					registered: false,
					createdAt: new Date(),
				});

				// Trigger the email notification Cloud Function
				try {
					const response = await fetch(
						"https://sendwelcomeemail-3guhnqcvsq-uc.a.run.app",
						{
							method: "POST",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({
								email: newEmail.toLowerCase(),
							}),
						}
					);

					if (!response.ok) {
						console.error("Failed to trigger email notification");
					}
				} catch (emailError) {
					console.error("Error sending email notification:", emailError);
				}

				setSuccess(`Successfully added ${newEmail}`);
				setNewEmail(""); // Clear the input
			} else {
				setError("Email already authorized");
			}
		} catch (error) {
			setError("Error adding email: " + error.message);
		}
		setLoading(false);
	};

	const processCSV = async () => {
		if (!file) return;
		setLoading(true);
		setError("");
		setSuccess("");

		Papa.parse(file, {
			complete: async (results) => {
				try {
					const emails = results.data.flat().filter((email) => email);

					for (const email of emails) {
						const q = query(
							collection(db, "authorizedEmails"),
							where("email", "==", email.toLowerCase())
						);
						const querySnapshot = await getDocs(q);

						if (querySnapshot.empty) {
							// Add the email to authorizedEmails collection
							await addDoc(collection(db, "authorizedEmails"), {
								email: email.toLowerCase(),
								registered: false,
								createdAt: new Date(),
							});

							// Trigger the email notification Cloud Function
							try {
								await fetch(
									"https://sendwelcomeemail-3guhnqcvsq-uc.a.run.app",
									{
										method: "POST",
										headers: {
											"Content-Type": "application/json",
										},
										body: JSON.stringify({
											email: email.toLowerCase(),
										}),
									}
								);
							} catch (emailError) {
								console.error("Error sending email notification:", emailError);
							}
						}
					}

					setSuccess(`Successfully processed ${emails.length} emails`);
					setFile(null);
				} catch (error) {
					setError("Error processing CSV: " + error.message);
				}
				setLoading(false);
			},
			error: (error) => {
				setError("Error parsing CSV: " + error.message);
				setLoading(false);
			},
		});
	};

	const handleModalSubmit = async () => {
		const isAuthorized = await handleAdminAuth();
		if (isAuthorized) {
			if (pendingAction === "email") {
				await addSingleEmail();
			} else if (pendingAction === "csv") {
				await processCSV();
			} else if (pendingAction === "delete") {
				await handleDeleteUsers();
			}
			setShowCredentialsModal(false);
			setAdminEmail("");
			setAdminPassword("");
		}
	};

	return (
		<div className="main-container">
			<div className="video-background">
				<video autoPlay loop muted playsInline>
					<source src="/images/49a01d274a.mp4" type="video/mp4" />
				</video>
			</div>

			<div className="auth-container">
				<h1>Admin Upload</h1>

				<div className="view-toggle">
					<button
						className={`toggle-btn ${activeView === "add" ? "active" : ""}`}
						onClick={() => setActiveView("add")}
					>
						Add Users
					</button>
					<button
						className={`toggle-btn ${activeView === "manage" ? "active" : ""}`}
						onClick={() => setActiveView("manage")}
					>
						Manage Users
					</button>
				</div>

				<div className="auth-form">
					{activeView === "add" ? (
						// Add Users View
						<>
							<div className="email-input-group">
								<input
									type="email"
									placeholder="Add Single Email"
									value={newEmail}
									onChange={(e) => setNewEmail(e.target.value)}
								/>
								<button
									onClick={() => handleActionRequest("email")}
									disabled={loading || !newEmail}
									className="add-email-btn"
								>
									Add Email
									<FontAwesomeIcon
										icon={faPlus}
										style={{ marginLeft: "10px" }}
									/>
								</button>
							</div>

							<div className="file-upload">
								<input
									type="file"
									accept=".csv"
									onChange={(e) => setFile(e.target.files[0])}
									id="csv-upload"
								/>
								<label htmlFor="csv-upload">
									{file ? file.name : "Choose CSV file"}
								</label>
							</div>
							<button
								onClick={() => handleActionRequest("csv")}
								disabled={loading || !file}
							>
								{loading ? "Processing..." : "Upload CSV"}
								<FontAwesomeIcon
									icon={faUpload}
									style={{ marginLeft: "10px" }}
								/>
							</button>
						</>
					) : (
						// Manage Users View
						<div className="authorized-users-list">
							<h2>Authorized Users</h2>
							{authorizedUsers.length > 0 && (
								<div className="users-actions">
									<button
										onClick={() => handleActionRequest("delete")}
										disabled={selectedUsers.size === 0}
										className="delete-selected-btn"
									>
										Delete Selected ({selectedUsers.size})
										<FontAwesomeIcon
											icon={faTrash}
											style={{ marginLeft: "10px" }}
										/>
									</button>
								</div>
							)}
							{authorizedUsers.map((user) => (
								<div key={user.id} className="authorized-user-item">
									<div className="user-info">
										<input
											type="checkbox"
											checked={selectedUsers.has(user.id)}
											onChange={() => handleUserSelection(user.id)}
											disabled={user.isAdmin || isAdminEmail(user.email)}
											className="user-checkbox"
										/>
										<span>{user.email}</span>
										{(user.isAdmin || isAdminEmail(user.email)) && (
											<span className="admin-badge">Admin</span>
										)}
									</div>
								</div>
							))}
						</div>
					)}

					{error && <div className="error">{error}</div>}
					{success && <div className="success">{success}</div>}

					<div className="auth-switch" style={{ marginTop: "20px" }}>
						<button
							onClick={() => navigate("/login")}
							className="secondary-button"
						>
							Go to Login
						</button>
					</div>
				</div>
			</div>

			{showCredentialsModal && (
				<div className="admin-modal-overlay">
					<div className="admin-modal-content">
						<h2>Enter Admin Credentials</h2>
						<input
							type="email"
							placeholder="Admin Email"
							value={adminEmail}
							onChange={(e) => setAdminEmail(e.target.value)}
						/>
						<input
							type="password"
							placeholder="Admin Password"
							value={adminPassword}
							onChange={(e) => setAdminPassword(e.target.value)}
						/>
						<div className="admin-modal-buttons">
							<button onClick={handleModalSubmit}>Submit</button>
							<button onClick={() => setShowCredentialsModal(false)}>
								Cancel
							</button>
						</div>
						{error && <div className="error">{error}</div>}
					</div>
				</div>
			)}
		</div>
	);
}

export default AdminUpload;
